/**
 * @retail-ops/shared-types/common
 *
 * Primitive types, envelope wrappers, pagination, error shapes.
 * Used by EVERY domain module.
 */

// -----------------------------------------------------------------------------
// Branded primitives
// -----------------------------------------------------------------------------

/** ISO-8601 string (e.g. "2026-04-21T15:30:00Z"). Branded for type safety. */
export type Iso8601 = string & { readonly __brand: 'Iso8601' };

/** Opaque GUID string. Branded for type safety. */
export type Guid = string & { readonly __brand: 'Guid' };

/** Core Director zero-padded 7-char CIF id (e.g. "0001234"). */
export type CifId = string & { readonly __brand: 'CifId' };

/** Core Director zero-padded account id. */
export type AcctId = string & { readonly __brand: 'AcctId' };

/**
 * Cast helper — use at trust boundaries (parser output, API ingress).
 * No runtime check; just narrows the string to the branded type.
 */
export const asIso8601 = (s: string): Iso8601 => s as Iso8601;
export const asGuid = (s: string): Guid => s as Guid;
export const asCifId = (s: string): CifId => s as CifId;
export const asAcctId = (s: string): AcctId => s as AcctId;

// -----------------------------------------------------------------------------
// Response envelopes
// -----------------------------------------------------------------------------

/**
 * v3 camelCase success envelope. Matches `/v3/*` routes.
 */
export interface ResponseEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
  errorCode?: string;
}

/**
 * Alias used in teams-app-wiring §3.2.
 */
export type ApiResult<T> = ResponseEnvelope<T>;

/**
 * Result with no payload — used for PATCH/POST that mutate only.
 */
export interface GenericResult {
  success: boolean;
  error?: string;
  errorCode?: string;
}

/**
 * Domain `/accounts/*` wrapper. The Azure Functions runtime serializes the
 * ObjectResult with System.Text.Json's default camelCase policy, so the WIRE
 * shape is camelCase {success,data,error,...} — NOT PascalCase. (The old
 * PascalCase declaration here was the root of a class of "Unable to load" /
 * "…failed-though-it-succeeded" bugs because tsc compiled reads against a
 * fiction. The inner `data` value is a raw jXRL JsonElement and keeps its own
 * PascalCase keys — that's a separate, correct passthrough.)
 */
export interface DomainResponseEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
  errorCode?: string;
  errorMessage?: string;
}

/**
 * v3 batch-aware error envelope.
 */
export interface ErrorEnvelope {
  success: false;
  error: string;
  errorCode?: string;
  errors?: ReadonlyArray<{ index: number; error: string; field?: string }>;
  retryAfter?: number;
}

/**
 * Pagination wrapper for list responses.
 */
export interface Paginated<T> {
  success: boolean;
  count: number;
  top?: number;
  items: ReadonlyArray<T>;
  continuationToken?: string;
}

// -----------------------------------------------------------------------------
// Error classification
// -----------------------------------------------------------------------------

/**
 * Error category used by api-client's classifyError helper.
 * Components render based on category, not raw HTTP status.
 */
export type ErrorCategory =
  | 'auth'
  | 'ratelimit'
  | 'validation'
  | 'upstream'
  | 'network'
  | 'unknown';

/**
 * Plain-object shape of an ApiError (matches the ApiError class in api-client).
 * Components that don't import the class still rely on this structural contract.
 */
export interface ApiError {
  httpStatus: number;
  category: ErrorCategory;
  code?: string;
  message: string;
  details?: unknown;
  retryAfter?: number;
  /** W3C traceparent of the failing request, for observability. */
  traceparent?: string;
  /** X-Correlation-Id echoed by the server. */
  correlationId?: string;
}

// -----------------------------------------------------------------------------
// Raw upstream shapes (pass-through)
// -----------------------------------------------------------------------------

/**
 * jXRL upstream envelope as seen raw via `/jxrl/*`.
 */
export interface RawJxResponse {
  ResponseHeader_CType?: {
    StatusCode: string;
    StatusDesc: string;
    Severity: 'Error' | 'Warning' | 'Info' | string;
  };
  Details?: ReadonlyArray<{
    Code: string;
    Category: string;
    Description: string;
  }>;
  RsStat?: 'Error' | 'Success' | string;
  // Anything else the upstream returns is preserved:
  [key: string]: unknown;
}

/** WAF block shape — proxy-synthesized when jXRL returns HTML 200. */
export interface WafBlockResponse {
  ResponseCode: 'WAF';
  Description: string;
  Details: ReadonlyArray<{ Code: 'WAF'; Category: 'Error'; Description: string }>;
}

/** SignNow domain error envelope. */
export interface SignNowErrorEnvelope {
  Success: false;
  Error: string;
  FailurePhase?: 'upload' | 'field-placement' | 'invite' | 'link-generation';
  RolledBackDocIds?: ReadonlyArray<string>;
  RolledBackGroupId?: string;
}

/** Dataverse domain error envelope (from /v3/qc/*). */
export interface DataverseErrorEnvelope {
  Success: false;
  Error: string;
  ErrorCode: string;
}

// -----------------------------------------------------------------------------
// Common request options
// -----------------------------------------------------------------------------

/**
 * Optional AbortSignal on every api-client method.
 * api-client's ApiClient.request() maps this onto fetch's AbortSignal.
 */
export interface RequestOptions {
  signal?: AbortSignal;
  /** Override default retry behavior. */
  retry?: boolean;
  /** Extra request headers. Merged on top of built-in ones. */
  headers?: Record<string, string>;
  /** Extra correlation id for the request (auto-generated if omitted). */
  correlationId?: string;
}

// -----------------------------------------------------------------------------
// AuditAware — every catalog row in v3 SQL carries this quad.
// Used as a TS interface mixin on entity-row types.
// -----------------------------------------------------------------------------
export interface AuditAware {
  createdAt?: string | null;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
}
